import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { 
  Search, Palette, Code, Video, Camera, PenTool, Edit3, Megaphone, Sparkles,
  Star, Briefcase, Users, Loader2, Filter, MapPin, Clock, Euro, 
  CheckCircle, Lock, ExternalLink, Phone, Mail
} from 'lucide-react';
import { cn } from '../../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SPECIALTIES = [
  { id: 'graphic-design', label: 'Design Graphique', icon: Palette, color: 'from-pink-500 to-rose-600' },
  { id: 'web-development', label: 'Développement Web', icon: Code, color: 'from-cyan-500 to-blue-600' },
  { id: 'video-production', label: 'Production Vidéo', icon: Video, color: 'from-purple-500 to-violet-600' },
  { id: 'motion-design', label: 'Motion Design', icon: Sparkles, color: 'from-amber-500 to-orange-600' },
  { id: 'photography', label: 'Photographie', icon: Camera, color: 'from-emerald-500 to-green-600' },
  { id: 'copywriting', label: 'Rédaction', icon: Edit3, color: 'from-blue-500 to-indigo-600' },
  { id: 'social-media', label: 'Social Media', icon: Megaphone, color: 'from-red-500 to-pink-600' },
  { id: 'illustration', label: 'Illustration', icon: PenTool, color: 'from-teal-500 to-cyan-600' },
];

const CreativeDirectoryPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState(null);

  useEffect(() => {
    if (user?.role === 'agency') {
      fetchCreatives();
    }
  }, [filterSpecialty, user]);

  const fetchCreatives = async () => {
    setLoading(true);
    try {
      const params = filterSpecialty ? `?specialty=${filterSpecialty}` : '';
      const response = await axios.get(`${API}/creatives/directory${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatives(response.data);
    } catch (error) {
      console.error('Error fetching creatives:', error);
      toast.error("Erreur lors du chargement de l'annuaire");
    } finally {
      setLoading(false);
    }
  };

  const getSpecialtyConfig = (id) => {
    return SPECIALTIES.find(s => s.id === id) || { label: id, icon: Palette, color: 'from-slate-500 to-slate-600' };
  };

  const filteredCreatives = creatives.filter(c => {
    if (!searchQuery) return true;
    const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
    const bio = (c.bio || '').toLowerCase();
    const specialties = (c.specialties || []).join(' ').toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) || 
           bio.includes(searchQuery.toLowerCase()) ||
           specialties.includes(searchQuery.toLowerCase());
  });

  // Redirect non-agency users
  if (user?.role !== 'agency') {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <Lock className="w-16 h-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Accès réservé</h2>
          <p className="text-slate-500 mb-4">L&apos;annuaire des créatifs est réservé à l&apos;agence MSC.</p>
          <Button onClick={() => navigate(-1)}>Retour</Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-cyan-500/10 text-cyan-600 border-cyan-500/30">
                Outil Interne
              </Badge>
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mt-2">Annuaire des Créatifs</h1>
            <p className="text-slate-500 text-sm mt-1">
              Trouvez le bon partenaire pour vos projets et proposez-lui une mission.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-slate-600">
              <Users className="w-3 h-3 mr-1" />
              {creatives.length} créatifs
            </Badge>
          </div>
        </div>

        {/* Search & Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Rechercher un créatif, une compétence..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Specialty Filters */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
                <button
                  onClick={() => setFilterSpecialty(null)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap transition-all flex-shrink-0 text-xs",
                    !filterSpecialty 
                      ? "bg-slate-900 text-white" 
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  )}
                >
                  <Filter className="w-3 h-3" />
                  Tous
                </button>
                {SPECIALTIES.map(spec => {
                  const Icon = spec.icon;
                  return (
                    <button
                      key={spec.id}
                      onClick={() => setFilterSpecialty(filterSpecialty === spec.id ? null : spec.id)}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap transition-all flex-shrink-0 text-xs",
                        filterSpecialty === spec.id
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      )}
                    >
                      <Icon className="w-3 h-3" />
                      {spec.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
          </div>
        )}

        {/* Creatives Grid */}
        {!loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCreatives.map((creative) => (
              <Card 
                key={creative.id} 
                className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => navigate(`/creatif/${creative.id}`)}
              >
                <CardContent className="p-0">
                  {/* Header with gradient */}
                  <div className={cn(
                    "h-20 relative bg-gradient-to-br",
                    creative.specialties?.[0] ? getSpecialtyConfig(creative.specialties[0]).color : "from-slate-400 to-slate-500"
                  )}>
                    {/* Status badges */}
                    <div className="absolute top-2 right-2 flex gap-1">
                      {creative.creative_type === 'integre' ? (
                        <Badge className="bg-emerald-500 text-white text-[10px]">
                          <CheckCircle className="w-3 h-3 mr-0.5" />
                          Intégré
                        </Badge>
                      ) : creative.has_active_mission ? (
                        <Badge className="bg-amber-500 text-white text-[10px]">
                          En mission
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-500 text-white text-[10px]">
                          Disponible
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Avatar */}
                  <div className="relative px-4 -mt-10">
                    <Avatar className="w-16 h-16 border-4 border-white shadow-md">
                      <AvatarImage src={creative.avatar_url} alt={creative.first_name} />
                      <AvatarFallback className="bg-slate-200 text-slate-600 text-lg font-bold">
                        {creative.first_name?.[0]}{creative.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4 pt-2">
                    <h3 className="font-bold text-slate-800 group-hover:text-cyan-600 transition-colors">
                      {creative.first_name} {creative.last_name}
                    </h3>
                    
                    {/* Specialties */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {creative.specialties?.slice(0, 3).map((spec, i) => {
                        const config = getSpecialtyConfig(spec);
                        return (
                          <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                            {config.label}
                          </Badge>
                        );
                      })}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400" />
                        <span className="font-medium text-slate-700">{creative.average_rating || 0}/20</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        <span>{creative.projects_completed || 0} projets</span>
                      </div>
                      {creative.hourly_rate > 0 && (
                        <div className="flex items-center gap-1">
                          <Euro className="w-3 h-3" />
                          <span>{creative.integrated_hourly_rate || creative.hourly_rate}€/h</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Quick actions */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      {creative.email && (
                        <a 
                          href={`mailto:${creative.email}`} 
                          onClick={e => e.stopPropagation()}
                          className="p-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                        >
                          <Mail className="w-3 h-3 text-slate-500" />
                        </a>
                      )}
                      {creative.phone && (
                        <a 
                          href={`tel:${creative.phone}`}
                          onClick={e => e.stopPropagation()}
                          className="p-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                        >
                          <Phone className="w-3 h-3 text-slate-500" />
                        </a>
                      )}
                      {creative.portfolio_url && (
                        <a 
                          href={creative.portfolio_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="p-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3 text-slate-500" />
                        </a>
                      )}
                      <div className="flex-1" />
                      <Button 
                        size="sm" 
                        className="text-xs h-7 bg-gradient-to-r from-cyan-500 to-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/creatif/${creative.id}`);
                        }}
                      >
                        Voir profil
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredCreatives.length === 0 && (
          <Card className="py-12">
            <div className="text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">Aucun créatif trouvé</h3>
              <p className="text-slate-500 text-sm">
                {searchQuery || filterSpecialty 
                  ? "Essayez de modifier vos critères de recherche."
                  : "Aucun créatif partenaire n'est encore enregistré."}
              </p>
            </div>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default CreativeDirectoryPage;
